export interface DischargeLimits {
  tss: number;
  bod5: number;
  cod: number;
  oilGrease: number;
  tds: number;
  tkn: number;
  totalNitrogen: number;
  totalPhosphorus: number;
  phMin: number;
  phMax: number;
  temperature: number;
  conductivity: number;
}

// Default limits mirror UK_EA (conservative) — used ONLY when no standard is resolved.
// Do NOT raise these; false compliance is a regulatory risk.
export const DEFAULT_LIMITS: DischargeLimits = {
  tss: 35,
  bod5: 25,
  cod: 125,
  oilGrease: 15,
  tds: 2000,
  tkn: 10,
  totalNitrogen: 10,
  totalPhosphorus: 1,
  phMin: 6.0,
  phMax: 9.0,
  temperature: 30,
  conductivity: 2500,
};

export interface WaterParameters {
  bod5: number;
  cod: number;
  tss: number;
  turbidity: number;
  temperature: number;
  color: number;
  oilGrease: number;
  freeOil: number;
  ph: number;
  tds: number;
  alkalinity: number;
  chlorides: number;
  ammoniaN: number;
  totalNitrogen: number;
  totalPhosphorus: number;
  tkn: number;
  lead: number;
  mercury: number;
  cadmium: number;
  chromium: number;
  arsenic: number;
  nickel: number;
  zinc: number;
  copper: number;
  sulfides: number;
  phenol: number;
  surfactants: number;
  flowRate: number;
  conductivity: number;
}

export interface DairyParameters {
  enabled: boolean;
  flowRate: number;
  bod5: number;
  cod: number;
  tss: number;
  oilGrease: number;
  ph: number;
  tds: number;
  totalNitrogen: number;
  totalPhosphorus: number;
  temperature: number;
  fatContent: number;
  lactose: number;
}

export interface BreweryParameters {
  enabled: boolean;
  flowRate: number;
  bod5: number;
  cod: number;
  tss: number;
  ph: number;
  tds: number;
  totalNitrogen: number;
  totalPhosphorus: number;
  temperature: number;
  alcoholContent: number;
  hopsResidue: number;
  yeastCount: number;
}

export interface BlendedParameters {
  source: 'base' | 'dairy' | 'brewery' | 'combined';
  label: string;
  params: WaterParameters;
  contributions: { name: string; flowRate: number; percentage: number }[];
}

export interface DosingChemical {
  id: string;
  name: string;
  formula: string;
  typicalDoseMin: number;
  typicalDoseMax: number;
  doseUnit: string;
  phChangePerMgL: number;
  bodReductionPercent: number;
  tssReductionPercent: number;
  codReductionPercent: number;
  color: string;
  description: string;
}

export interface DosingConfig {
  enabled: boolean;
  chemicalId: string;
  dose: number;
  targetPh?: number;
}

export interface EquipmentItem {
  id: string;
  name: string;
  category: 'Pre-Treatment' | 'Primary' | 'Secondary' | 'Tertiary' | 'Polishing' | 'Sludge' | 'Disinfection';
  type: string;
  purpose: string;
  bodReduction: number;
  codReduction: number;
  tssReduction: number;
  ogReduction: number;
  tnReduction: number;
  tpReduction: number;
  tdsChange: number;
  phChange: number;
  minFlow: number;
  maxFlow: number;
  typicalHRT: number;
  footprint: 'Very Small' | 'Small' | 'Medium' | 'Large' | 'Very Large';
  energyUse: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';
  capitalCost: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';
  operatingCost: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';
  salineCompatible: boolean;
  description: string;
  notes: string;
  // Lamella clarifier specific
  lamellaConfig?: {
    plateAngle: number; // degrees, typically 60
    plateSpacing: number; // mm, typically 50-80
    plateLength: number; // m, typically 1.0-2.5
    projectedAreaFactor: number; // multiplier for settlement area
    maxSurfaceLoading: number; // m/hr
    typicalRemovalRate: number; // %
  };
}

export interface LamellaSettlementCalc {
  equipmentId: string;
  flowRateM3PerDay: number;
  flowRateM3PerHour: number;
  requiredSettlingAreaM2: number;
  projectedSettlingAreaM2: number;
  actualTankAreaM2: number;
  surfaceLoadingRateMPerHr: number;
  plateCount: number;
  plateSpacingMm: number;
  plateAngleDegrees: number;
  hydraulicRetentionTimeMin: number;
  estimatedTSSRemovalPercent: number;
  estimatedBODRemovalPercent: number;
  compliance: 'Pass' | 'Marginal' | 'Fail';
  notes: string;
}

export interface ProcessStage {
  stageNumber: number;
  stageName: string;
  equipment: EquipmentItem[];
  flowRate: number;
  ph: number;
  bod5: number;
  cod: number;
  tss: number;
  tds: number;
  oilGrease: number;
  totalNitrogen: number;
  totalPhosphorus: number;
  conductivity: number;
  temperature: number;
  tkn: number;
  bodReductionSoFar: number;
  codReductionSoFar: number;
  tssReductionSoFar: number;
  cumulativeVolumeM3: number;
  chemicals: { name: string; dose: number; unit: string }[];
  tanks: ProcessTank[];
  sludgeGeneration?: {
    volumeM3PerDay: number;
    drySolidsKgPerDay: number;
    moisturePercent: number;
    description: string;
  };
}

export interface ProcessTank {
  id: string;
  name: string;
  type: string;
  purpose: string;
  volumeM3: number;
  dimensions: string;
  hrt: number;
  notes: string;
}

export interface ProcessSimulation {
  stages: ProcessStage[];
  totalBodReduction: number;
  totalCodReduction: number;
  totalTssReduction: number;
  finalPh: number;
  totalTanks: ProcessTank[];
  totalTankVolumeM3: number;
  totalTankVolumeLitres: number;
  equipmentCount: number;
  lamellaSettlement?: LamellaSettlementCalc;
}

export interface ParameterClassification {
  parameter: string;
  value: number;
  level: string;
  classification: string;
  score: number;
}

export interface ToxicityItem {
  toxicant: string;
  value: number;
  threshold: number;
  status: string;
  risk: string;
}

export interface PreTreatmentItem {
  step: string;
  trigger: string;
  status: string;
  inputValue: number;
  priority: number;
}

export interface BiologicalViabilityItem {
  factor: string;
  value: string | number;
  criterion: string;
  assessment: string;
  passFail: 'PASS' | 'FAIL';
}

export interface ProcessCompatibility {
  process: string;
  type: 'Biological' | 'Physicochemical';
  score: number;
  compatibilityScore: number;
  compatibility: string;
  recommendation: string;
  reason: string;
  caveats?: string;
  confidence?: string;
}

export interface TreatmentTrainStep {
  stage: string;
  process: string;
  scoreOrStatus: string;
  purpose: string;
}

export interface KeyFinding {
  metric: string;
  result: string;
  rating: string;
  implication: string;
}

export interface ClothFilterAssessment {
  suitabilityScore: number;
  suitability: string;
  recommendation: string;
  pros: { aspect: string; detail: string }[];
  cons: { aspect: string; detail: string }[];
  comparisons: { technology: string; advantage: string; whenOthersWin: string }[];
  verdict: string;
  bodReduction: string;
  tssRemoval: string;
  energyUse: string;
  idealFor: string;
  notSuitableFor: string;
}

export interface SalinityAssessment {
  conductivity: number;
  tds: number;
  tdsConductivityRatio: number;
  estimatedSalinityPPT: number;
  estimatedSalinityMG: number;
  classification: string;
  probabilityPercent: number;
  probabilityLabel: string;
  confidenceLevel: string;
  freshWaterProbability: number;
  brackishProbability: number;
  salineProbability: number;
  hypersalineProbability: number;
  warnings: string[];
  biologicalImpact: string;
  compatibleProcesses: string[];
  incompatibleProcesses: string[];
  recommendedTreatment: string;
}

export interface SavedSample {
  id: string;
  name: string;
  date: string;
  params: WaterParameters;
  dairy?: DairyParameters;
  brewery?: BreweryParameters;
  dosings?: DosingConfig[];
  assessmentResult?: AssessmentResult | null;
}

export interface EquipmentDosingReq {
  equipmentName: string;
  equipmentId: string;
  requiresCoagulant: boolean;
  requiresFlocculant: boolean;
  requiresPhAdjustment: boolean;
  recommendedChemicals: { chemicalId: string; name: string; formula: string; reason: string; recommendedDose: number; doseUnit: string }[];
  severity: 'required' | 'recommended' | 'optional';
  reason: string;
}

export interface AssessmentResult {
  parameterClassification: ParameterClassification[];
  toxicityAssessment: ToxicityItem[];
  toxicParametersCount: number;
  overallTreatabilityIndex: number;
  treatabilityClassification: string;
  preTreatmentRequirements: PreTreatmentItem[];
  biologicalViability: BiologicalViabilityItem[];
  biologicalViabilityScore: string;
  recommendedApproach: string;
  processCompatibility: ProcessCompatibility[];
  treatmentTrain: TreatmentTrainStep[];
  keyFindings: KeyFinding[];
  clothFilter: ClothFilterAssessment;
  salinity: SalinityAssessment;
  blended?: BlendedParameters;
  simulation?: ProcessSimulation;
  dosingConfig?: DosingConfig[];
  recommendedDosings?: { chemicalId: string; name: string; formula: string; recommendedDose: number; doseUnit: string; reason: string }[];
  dosingWarning?: {
    show: boolean;
    message: string;
    severity: 'info' | 'warning' | 'critical';
    affectedStages: string[];
  };
  equipmentDosingRequirements?: EquipmentDosingReq[];
  equipmentValidation?: Array<{
    equipmentId: string; equipmentName: string;
    stage: string; triggerParam: string;
    triggerValue: string; threshold: string;
    selected: boolean; passes: boolean;
    principle: string; formula?: string;
  }>;
}

export const AVAILABLE_CHEMICALS: DosingChemical[] = [
  {
    id: 'fecl3',
    name: 'Ferric Chloride',
    formula: 'FeCl3',
    typicalDoseMin: 50,
    typicalDoseMax: 500,
    doseUnit: 'mg/L',
    phChangePerMgL: -0.0012,
    bodReductionPercent: 25,
    tssReductionPercent: 85,
    codReductionPercent: 30,
    color: 'text-yellow-400',
    description: 'Strong coagulant. Effective for phosphorus removal and turbidity. Lowers pH significantly. Dark orange-brown solution.'
  },
  {
    id: 'alum',
    name: 'Aluminium Sulfate',
    formula: 'Al2(SO4)3',
    typicalDoseMin: 30,
    typicalDoseMax: 400,
    doseUnit: 'mg/L',
    phChangePerMgL: -0.0008,
    bodReductionPercent: 20,
    tssReductionPercent: 80,
    codReductionPercent: 25,
    color: 'text-blue-400',
    description: 'Most common coagulant. Good for moderate turbidity and color removal. Slight pH reduction. Clear solution.'
  },
  {
    id: 'pac',
    name: 'Polyaluminium Chloride',
    formula: 'PAC',
    typicalDoseMin: 20,
    typicalDoseMax: 300,
    doseUnit: 'mg/L',
    phChangePerMgL: -0.0005,
    bodReductionPercent: 22,
    tssReductionPercent: 82,
    codReductionPercent: 28,
    color: 'text-cyan-400',
    description: 'Pre-polymerized coagulant. Lower dose than alum, wider pH operating range (6.5-8.5). Less pH impact.'
  },
  {
    id: 'feso4',
    name: 'Ferrous Sulfate',
    formula: 'FeSO4',
    typicalDoseMin: 40,
    typicalDoseMax: 400,
    doseUnit: 'mg/L',
    phChangePerMgL: -0.0006,
    bodReductionPercent: 18,
    tssReductionPercent: 75,
    codReductionPercent: 22,
    color: 'text-green-400',
    description: 'Economical iron salt. Requires oxidation to ferric for best results. Less pH impact than ferric chloride. Greenish crystals.'
  },
  {
    id: 'lime',
    name: 'Hydrated Lime',
    formula: 'Ca(OH)2',
    typicalDoseMin: 50,
    typicalDoseMax: 800,
    doseUnit: 'mg/L',
    phChangePerMgL: 0.0025,
    bodReductionPercent: 10,
    tssReductionPercent: 60,
    codReductionPercent: 15,
    color: 'text-slate-400',
    description: 'pH adjustment and coagulation aid. Increases alkalinity and pH. Used with iron/alum coagulants for phosphorus removal. White powder.'
  },
  {
    id: 'caustic',
    name: 'Caustic Soda',
    formula: 'NaOH',
    typicalDoseMin: 20,
    typicalDoseMax: 500,
    doseUnit: 'mg/L',
    phChangePerMgL: 0.004,
    bodReductionPercent: 0,
    tssReductionPercent: 0,
    codReductionPercent: 0,
    color: 'text-red-400',
    description: 'Strong base for pH neutralization only. No coagulation effect. Rapid pH increase. Handle with care. Clear solution.'
  },
  {
    id: 'polymer',
    name: 'Polymer Flocculant',
    formula: 'Polyacrylamide',
    typicalDoseMin: 0.5,
    typicalDoseMax: 10,
    doseUnit: 'mg/L',
    phChangePerMgL: 0,
    bodReductionPercent: 5,
    tssReductionPercent: 90,
    codReductionPercent: 8,
    color: 'text-purple-400',
    description: 'Flocculation aid used WITH coagulant. Improves settling and DAF performance. No pH change. Very low dose. White powder or emulsion.'
  },
  {
    id: 'chlorine',
    name: 'Chlorine',
    formula: 'Cl2 / NaOCl',
    typicalDoseMin: 5,
    typicalDoseMax: 50,
    doseUnit: 'mg/L',
    phChangePerMgL: -0.001,
    bodReductionPercent: 8,
    tssReductionPercent: 10,
    codReductionPercent: 12,
    color: 'text-orange-400',
    description: 'Disinfection and oxidation. Can oxidize some organics. Slight pH reduction. Used for odor control and final disinfection.'
  }
];

export const EQUIPMENT_LIBRARY: EquipmentItem[] = [
  // PRE-TREATMENT
  {
    id: 'eq_tank', name: 'Equalization Tank', category: 'Pre-Treatment', type: 'Concrete / Steel',
    purpose: 'Flow balancing, storm buffering', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 100000, typicalHRT: 12, footprint: 'Large', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Provides hydraulic and organic loading equalization. Essential for variable flows.',
    notes: '12-24 hour HRT. Mixer and level control. Can include aeration for odor control.'
  },
  {
    id: 'screen_fine', name: 'Fine Screen', category: 'Pre-Treatment', type: 'Mechanical',
    purpose: 'Remove solids > 3-6mm', bodReduction: 0.02, codReduction: 0.01, tssReduction: 0.05, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 50000, typicalHRT: 0.1, footprint: 'Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Step screen, drum screen, or bar screen. Protects downstream equipment.',
    notes: '3-6mm openings. Automatic rake and compactor. Screenings to disposal.'
  },
  {
    id: 'grit_vortex', name: 'Vortex Grit Chamber', category: 'Pre-Treatment', type: 'Vortex / Aerated',
    purpose: 'Remove sand, grit, glass', bodReduction: 0, codReduction: 0, tssReduction: 0.05, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 1, footprint: 'Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Tangential flow creates vortex. Heavy particles settle.',
    notes: '1-2 min HRT. Grit classifier and washer. Protects pumps from abrasion.'
  },
  {
    id: 'grit_aerated', name: 'Aerated Grit Chamber', category: 'Pre-Treatment', type: 'Rectangular',
    purpose: 'Sand and grit removal with aeration', bodReduction: 0, codReduction: 0, tssReduction: 0.03, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 100, maxFlow: 100000, typicalHRT: 3, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Cross-flow aeration keeps organics in suspension while grit settles.',
    notes: '2-5 min HRT. Air flow 4-8 Nm3/h per m length. Grit pump to classifier.'
  },
  {
    id: 'screening_washer', name: 'Screening Washer / Compactor', category: 'Pre-Treatment', type: 'Screw / Hydraulic',
    purpose: 'Wash and compact screenings', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 50000, typicalHRT: 0, footprint: 'Very Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Washes organics off screenings and compacts to 40% DS.',
    notes: 'Reduces screenings volume by 60-80%. Wash water returned to inlet.'
  },

  // PRIMARY - PHYSICOCHEMICAL
  {
    id: 'daf_rect', name: 'DAF (Rectangular)', category: 'Primary', type: 'Rectangular',
    purpose: 'Oil, grease & solids flotation', bodReduction: 0.40, codReduction: 0.35, tssReduction: 0.85, ogReduction: 0.90,
    tnReduction: 0.05, tpReduction: 0.20, tdsChange: 0.02, phChange: 0,
    minFlow: 50, maxFlow: 20000, typicalHRT: 0.5, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Dissolved air flotation with micro-bubbles. Excellent for O&G and light solids.',
    notes: '0.5-1 hr HRT. Recycle flow 8-15%. Surface skimmer + bottom scraper.'
  },
  {
    id: 'daf_circular', name: 'DAF (Circular)', category: 'Primary', type: 'Circular',
    purpose: 'High-rate flotation', bodReduction: 0.35, codReduction: 0.30, tssReduction: 0.80, ogReduction: 0.85,
    tnReduction: 0.05, tpReduction: 0.15, tdsChange: 0.02, phChange: 0,
    minFlow: 100, maxFlow: 50000, typicalHRT: 0.5, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Circular DAF with rotating skimmer. Higher hydraulic loading.',
    notes: '0.5-1 hr HRT. Surface loading 5-15 m/hr. Central feed, peripheral discharge.'
  },
  {
    id: 'api_sep', name: 'API Oil-Water Separator', category: 'Primary', type: 'Gravity',
    purpose: 'Free oil and grit separation', bodReduction: 0.15, codReduction: 0.10, tssReduction: 0.20, ogReduction: 0.70,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 2, footprint: 'Large', energyUse: 'Very Low', capitalCost: 'Medium', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Parallel plate or open channel separator for free oil droplets.',
    notes: '2-4 hr HRT. Corrugated plate packs for enhanced separation. Oil skimmer.'
  },
  {
    id: 'coag_basin', name: 'Coagulation / Flocculation Basin', category: 'Primary', type: 'Baffled / Mechanized',
    purpose: 'Chemical precipitation & flocculation', bodReduction: 0.20, codReduction: 0.25, tssReduction: 0.80, ogReduction: 0.15,
    tnReduction: 0.05, tpReduction: 0.75, tdsChange: 0.02, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 1, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Flash mix + flocculation + settling. Complete physicochemical treatment.',
    notes: 'Flash mix 1-3 min (150-300 rpm). Flocculation 15-30 min (20-40 rpm). Chemical dosing control.'
  },
  {
    id: 'sedimentation_pri', name: 'Primary Sedimentation', category: 'Primary', type: 'Circular / Rectangular',
    purpose: 'Settleable solids removal', bodReduction: 0.25, codReduction: 0.20, tssReduction: 0.60, ogReduction: 0.30,
    tnReduction: 0.05, tpReduction: 0.05, tdsChange: 0, phChange: -0.1,
    minFlow: 100, maxFlow: 100000, typicalHRT: 2, footprint: 'Large', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Gravity settling of suspended solids. Simple and reliable.',
    notes: '1.5-2.5 hr HRT. Surface overflow rate < 1.5 m/hr. Scraper mechanism.'
  },
  {
    id: 'lamella_clarifier', name: 'Lamella Clarifier', category: 'Primary', type: 'Inclined Plate',
    purpose: 'High-rate solids settling', bodReduction: 0.30, codReduction: 0.25, tssReduction: 0.85, ogReduction: 0.25,
    tnReduction: 0.05, tpReduction: 0.10, tdsChange: 0, phChange: -0.05,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.5, footprint: 'Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: '60-degree inclined plates increase settling area 5-10x. Very compact.',
    notes: '0.5-1 hr HRT. Surface loading 5-10 m/hr. Plate spacing 50-80mm. Sludge hopper.',
    lamellaConfig: {
      plateAngle: 60,
      plateSpacing: 60,
      plateLength: 1.5,
      projectedAreaFactor: 8.0,
      maxSurfaceLoading: 10.0,
      typicalRemovalRate: 85.0,
    },
  },

  // SECONDARY - BIOLOGICAL
  {
    id: 'electrocoagulation', name: 'Electrocoagulation Cell', category: 'Primary', type: 'Electrolytic Reactor',
    purpose: 'Saline-safe solids and BOD removal via electro-flocculation', bodReduction: 0.50, codReduction: 0.45, tssReduction: 0.90, ogReduction: 0.85,
    tnReduction: 0.15, tpReduction: 0.85, tdsChange: 0, phChange: -0.1,
    minFlow: 10, maxFlow: 5000, typicalHRT: 1, footprint: 'Small', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Sacrificial iron/aluminium anodes create in-situ coagulant. Excellent for saline, brewery, and oily wastewater. No external chemicals.',
    notes: 'Current density 20-100 A/m2. 30-60 min HRT. Electrode consumption 5-15 g/m3. Sludge is metal hydroxide-rich. Proven for high-salinity industrial wastewater in Middle East and China.'
  },
  {
    id: 'wet_air_oxidation', name: 'Wet Air Oxidation (WAO)', category: 'Primary', type: 'High-Pressure Reactor',
    purpose: 'Destroy high-COD organics at elevated T/P', bodReduction: 0.75, codReduction: 0.80, tssReduction: 0.30, ogReduction: 0.70,
    tnReduction: 0.30, tpReduction: 0.20, tdsChange: 0, phChange: -0.3,
    minFlow: 10, maxFlow: 10000, typicalHRT: 2, footprint: 'Medium', energyUse: 'Very High', capitalCost: 'Very High', operatingCost: 'Very High',
    salineCompatible: true,
    description: 'Aqueous phase oxidation with compressed air at 125-320C and 2-20 MPa. Destroys refractory and high-strength organics regardless of salinity.',
    notes: 'For COD > 10,000 mg/L. Thermal recovery via heat exchangers. Proven for pharmaceutical, chemical, and high-salinity industrial effluents. No biological limitations.'
  },

  // SECONDARY - BIOLOGICAL
  {
    id: 'halophilic_mbbr', name: 'Halophilic MBBR', category: 'Secondary', type: 'Salt-Tolerant Biofilm Carriers',
    purpose: 'Biological treatment in saline conditions', bodReduction: 0.82, codReduction: 0.78, tssReduction: 0.30, ogReduction: 0.20,
    tnReduction: 0.35, tpReduction: 0.25, tdsChange: -0.01, phChange: -0.1,
    minFlow: 50, maxFlow: 50000, typicalHRT: 14, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'High', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Specialised MBBR with halophilic (salt-loving) biofilm cultivated on HDPE carriers. Proven for saline wastewater up to 50,000 mg/L TDS in UAE, Israel, and China.',
    notes: '10-18 hr HRT. Acclimation period 4-8 weeks. Carrier fill 50-65%. Fine bubble aeration essential. BIOFILM IS SALT-TOLERANT — operates where standard MBBR fails.'
  },
  {
    id: 'abr', name: 'Anaerobic Baffled Reactor (ABR)', category: 'Secondary', type: 'Multi-Compartment Anaerobic',
    purpose: 'High-strength anaerobic treatment with salinity tolerance', bodReduction: 0.70, codReduction: 0.65, tssReduction: 0.50, ogReduction: 0.25,
    tnReduction: 0.10, tpReduction: 0.10, tdsChange: 0, phChange: 0.1,
    minFlow: 50, maxFlow: 50000, typicalHRT: 20, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Medium', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Anaerobic baffled reactor with 3-6 compartments. More robust to hydraulic and organic shock than UASB. Tolerates moderate salinity better than granular sludge systems. Proven for brewery and food wastewater.',
    notes: '12-24 hr HRT. Upflow velocity 0.5-1.0 m/hr. Biogas 60% CH4. Compartmentalisation prevents biomass washout. Handles high-suspended solids from brewery better than UASB.'
  },
  {
    id: 'saf', name: 'Submerged Aerated Filter (SAF)', category: 'Secondary', type: 'Upflow Submerged Media',
    purpose: 'Fixed-film biological in compact footprint', bodReduction: 0.85, codReduction: 0.80, tssReduction: 0.35, ogReduction: 0.15,
    tnReduction: 0.35, tpReduction: 0.20, tdsChange: -0.01, phChange: -0.15,
    minFlow: 50, maxFlow: 50000, typicalHRT: 6, footprint: 'Small', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Upflow submerged granular or plastic media with aeration from below. Fixed biofilm is more resilient to salinity shock than suspended growth. Proven for high-strength industrial wastewater in UK and Europe.',
    notes: '4-8 hr HRT. Media depth 2-4m. Backwash every 24h. Air scour + water wash. Can operate at up to 15,000 mg/L TDS with acclimated biofilm.',
  },
  {
    id: 'activated_sludge', name: 'Activated Sludge (Conventional)', category: 'Secondary', type: 'Aeration + Clarifier',
    purpose: 'Biological oxidation of organics', bodReduction: 0.90, codReduction: 0.85, tssReduction: 0.30, ogReduction: 0.10,
    tnReduction: 0.40, tpReduction: 0.30, tdsChange: -0.01, phChange: -0.2,
    minFlow: 100, maxFlow: 100000, typicalHRT: 18, footprint: 'Large', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: false,
    description: 'Aerobic suspended growth with biomass recycle. Most common biological process.',
    notes: '18-24 hr HRT. MLSS 3000-4000 mg/L. F/M 0.2-0.5 kg BOD/kg MLSS.day. DO 2-3 mg/L.'
  },
  {
    id: 'extended_aeration', name: 'Extended Aeration', category: 'Secondary', type: 'Aeration + Clarifier',
    purpose: 'Low F/M biological treatment', bodReduction: 0.92, codReduction: 0.88, tssReduction: 0.35, ogReduction: 0.10,
    tnReduction: 0.50, tpReduction: 0.40, tdsChange: -0.01, phChange: -0.2,
    minFlow: 50, maxFlow: 50000, typicalHRT: 24, footprint: 'Large', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: false,
    description: 'Modified activated sludge with 18-24 hr detention. Lower sludge production.',
    notes: '24 hr HRT. MLSS 3500-4500 mg/L. F/M 0.05-0.15. Good for small communities.'
  },
  {
    id: 'sbr', name: 'SBR (Sequencing Batch Reactor)', category: 'Secondary', type: 'Fill-Draw Batch',
    purpose: 'Variable flow biological treatment', bodReduction: 0.92, codReduction: 0.88, tssReduction: 0.40, ogReduction: 0.10,
    tnReduction: 0.60, tpReduction: 0.50, tdsChange: -0.01, phChange: -0.2,
    minFlow: 50, maxFlow: 50000, typicalHRT: 20, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: false,
    description: 'Single-tank batch process combining reaction and settling.',
    notes: 'Fill-react-settle-decide-idle cycle. Cycle time 4-8 hours. No secondary clarifier needed.'
  },
  {
    id: 'mbr', name: 'MBR (Membrane Bioreactor)', category: 'Secondary', type: 'Aeration + Membrane',
    purpose: 'High-quality effluent with membranes', bodReduction: 0.95, codReduction: 0.92, tssReduction: 0.95, ogReduction: 0.20,
    tnReduction: 0.55, tpReduction: 0.45, tdsChange: -0.01, phChange: -0.1,
    minFlow: 50, maxFlow: 50000, typicalHRT: 12, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: false,
    description: 'Combines biological treatment with submerged membrane filtration.',
    notes: '12-18 hr HRT. MLSS 8000-12000 mg/L. Flux 15-25 LMH. CIP every 3-6 months.'
  },
  {
    id: 'mbbr', name: 'MBBR (Moving Bed Biofilm)', category: 'Secondary', type: 'Biofilm Carriers',
    purpose: 'Attached growth on moving carriers', bodReduction: 0.88, codReduction: 0.82, tssReduction: 0.25, ogReduction: 0.10,
    tnReduction: 0.35, tpReduction: 0.25, tdsChange: -0.01, phChange: -0.15,
    minFlow: 50, maxFlow: 50000, typicalHRT: 8, footprint: 'Small', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: false,
    description: 'Plastic carriers with biofilm in aerated tank. No sludge recycle.',
    notes: '6-12 hr HRT. Carrier fill 50-70%. Fine bubble aeration. Screens retain carriers.'
  },
  {
    id: 'trickling_filter', name: 'Trickling Filter', category: 'Secondary', type: 'Fixed Media',
    purpose: 'Attached growth biofilm process', bodReduction: 0.80, codReduction: 0.75, tssReduction: 0.30, ogReduction: 0.10,
    tnReduction: 0.20, tpReduction: 0.15, tdsChange: -0.01, phChange: -0.1,
    minFlow: 50, maxFlow: 100000, typicalHRT: 1.5, footprint: 'Large', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: false,
    description: 'Rock or plastic media with biofilm. Low energy, simple operation.',
    notes: '1.5-2.5 hr contact time. Organic loading 0.5-1.5 kg/m3.day. Natural ventilation.'
  },
  {
    id: 'uasb', name: 'UASB (Upflow Anaerobic)', category: 'Secondary', type: 'Anaerobic',
    purpose: 'High-strength anaerobic treatment', bodReduction: 0.75, codReduction: 0.70, tssReduction: 0.40, ogReduction: 0.30,
    tnReduction: 0.10, tpReduction: 0.10, tdsChange: 0, phChange: 0.1,
    minFlow: 100, maxFlow: 100000, typicalHRT: 12, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Medium', operatingCost: 'Very Low',
    salineCompatible: false,
    description: 'Upflow anaerobic sludge blanket. Produces biogas.',
    notes: '8-24 hr HRT. Granular sludge bed. Gas collection dome. COD loading 5-15 kg/m3.day.'
  },
  {
    id: 'constructed_wetland', name: 'Constructed Wetland', category: 'Secondary', type: 'Natural',
    purpose: 'Natural biological treatment', bodReduction: 0.80, codReduction: 0.70, tssReduction: 0.50, ogReduction: 0.20,
    tnReduction: 0.40, tpReduction: 0.35, tdsChange: -0.01, phChange: 0,
    minFlow: 10, maxFlow: 5000, typicalHRT: 72, footprint: 'Very Large', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: false,
    description: 'Subsurface flow with plants and soil microbiology.',
    notes: '3-5 day HRT. Gravel bed 0.6m deep. Cattails/reeds. Very low operating cost.'
  },

  // TERTIARY - FILTRATION
  {
    id: 'forward_osmosis', name: 'Forward Osmosis (FO)', category: 'Tertiary', type: 'Osmotic Membrane',
    purpose: 'Draw water through membrane using osmotic gradient', bodReduction: 0.25, codReduction: 0.40, tssReduction: 0.99, ogReduction: 0.60,
    tnReduction: 0.60, tpReduction: 0.70, tdsChange: -0.30, phChange: 0,
    minFlow: 10, maxFlow: 20000, typicalHRT: 0.5, footprint: 'Small', energyUse: 'Medium', capitalCost: 'High', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Uses osmotic pressure gradient to draw water across semi-permeable membrane into concentrated draw solution. Works at very high feed salinity where RO fails. Proven for landfill leachate, produced water, and high-salinity industrial wastewater.',
    notes: 'Low hydraulic pressure < 5 bar. Draw solution regeneration required. TFC membranes common. Pre-treatment with UF/MF essential. Emerging but proven in niche high-salinity applications.'
  },
  {
    id: 'membrane_distillation', name: 'Membrane Distillation', category: 'Tertiary', type: 'Thermal Membrane',
    purpose: 'Thermal separation for saline + organics', bodReduction: 0.20, codReduction: 0.35, tssReduction: 0.99, ogReduction: 0.70,
    tnReduction: 0.30, tpReduction: 0.40, tdsChange: -0.85, phChange: 0,
    minFlow: 5, maxFlow: 5000, typicalHRT: 1, footprint: 'Medium', energyUse: 'Very High', capitalCost: 'High', operatingCost: 'Very High',
    salineCompatible: true,
    description: 'Vapour passes through hydrophobic membrane from hot feed to cold permeate. Rejects 100% non-volatiles including salts, organics, and pathogens. Operates at low temperature (40-80C). Proven for seawater, brines, and high-TDS wastewater.',
    notes: 'Hydrophobic PTFE/PP membrane. Feed 60-80C. Permeate 20-30C. Can use waste heat. Excellent for high-salinity + organics where RO fouls. Solar-thermal coupling proven in Australia and Middle East.'
  },
  {
    id: 'mmf_gravity', name: 'Multimedia Filter (Gravity)', category: 'Tertiary', type: 'Gravity Depth',
    purpose: 'TSS/BOD depth filtration', bodReduction: 0.20, codReduction: 0.10, tssReduction: 0.90, ogReduction: 0.10,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.3, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Anthracite + sand + gravel layers. Backwash every 12-24h.',
    notes: '10-20 m/hr filtration rate. Dual media or triple media. Air scour + water wash.'
  },
  {
    id: 'mmf_pressure', name: 'Pressure Multimedia Filter', category: 'Tertiary', type: 'Pressure Vessel',
    purpose: 'High-rate TSS removal', bodReduction: 0.20, codReduction: 0.10, tssReduction: 0.95, ogReduction: 0.10,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 100, maxFlow: 50000, typicalHRT: 0.2, footprint: 'Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Steel vessel at 2-6 bar. Higher rates than gravity.',
    notes: '15-25 m/hr rate. Automatic backwash valve nest. Used as pre-treatment for membranes.'
  },
  {
    id: 'cloth_filter', name: 'Cloth / Disk Filter', category: 'Tertiary', type: 'Woven Mesh',
    purpose: 'Low-energy solids capture', bodReduction: 0.25, codReduction: 0.15, tssReduction: 0.75, ogReduction: 0.15,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 10000, typicalHRT: 0.1, footprint: 'Small', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Rotating cloth or disk panels. Automatic backwash.',
    notes: '10-30 micron effective. 0.5-2 kWh/m3. Media replacement 3-5 years. Good for small plants.'
  },
  {
    id: 'sand_filter', name: 'Rapid Sand Filter', category: 'Tertiary', type: 'Gravity / Pressure',
    purpose: 'Single-media depth filtration', bodReduction: 0.10, codReduction: 0.05, tssReduction: 0.80, ogReduction: 0.05,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.3, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Single silica sand media. Simple and proven technology.',
    notes: '5-15 m/hr rate. 0.5-1m sand depth. Backwash 2-3x throughput. Smaller than MMF.'
  },
  {
    id: 'dual_media', name: 'Dual Media Filter', category: 'Tertiary', type: 'Depth',
    purpose: 'Anthracite + sand filtration', bodReduction: 0.15, codReduction: 0.08, tssReduction: 0.85, ogReduction: 0.10,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.3, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Two-layer filter with coarse anthracite over finer sand.',
    notes: '8-15 m/hr rate. Better solids holding than single sand. Common in municipal plants.'
  },
  {
    id: 'gac_contactor', name: 'GAC Contactor', category: 'Tertiary', type: 'Adsorption Vessel',
    purpose: 'Dissolved organics removal', bodReduction: 0.35, codReduction: 0.50, tssReduction: 0.10, ogReduction: 0.20,
    tnReduction: 0, tpReduction: 0, tdsChange: -0.01, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.5, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Granular activated carbon for refractory organics, phenol, color.',
    notes: 'EBCT 15-30 min. Thermal reactivation 12-24 months. Multiple vessels for switch-out.'
  },
  {
    id: 'pac_dosing', name: 'PAC Dosing (Powdered AC)', category: 'Tertiary', type: 'Inline Adsorption',
    purpose: 'Powdered carbon for organics', bodReduction: 0.25, codReduction: 0.35, tssReduction: 0.20, ogReduction: 0.15,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.1, footprint: 'Very Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'High',
    salineCompatible: true,
    description: 'Powdered activated carbon dosed inline. Single-use.',
    notes: 'Dose 10-50 mg/L. Contact time 15-30 min. Settled with sludge. No regeneration.'
  },
  {
    id: 'ion_exchange', name: 'Ion Exchange', category: 'Tertiary', type: 'Resin Vessels',
    purpose: 'Selective ion removal', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0.50, tpReduction: 0.80, tdsChange: -0.05, phChange: 0,
    minFlow: 50, maxFlow: 20000, typicalHRT: 0.3, footprint: 'Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Resin beads for specific ions: nitrate, arsenic, metals, hardness.',
    notes: 'Regenerable with acid/base. 2-4 vessels for continuous operation. Brine disposal.'
  },
  {
    id: 'ro_system', name: 'RO System (Reverse Osmosis)', category: 'Tertiary', type: 'Membrane',
    purpose: 'Dissolved solids removal', bodReduction: 0.10, codReduction: 0.30, tssReduction: 0.99, ogReduction: 0.30,
    tnReduction: 0.70, tpReduction: 0.70, tdsChange: -0.90, phChange: -0.3,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.2, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'Spiral-wound membranes for desalination and organics removal.',
    notes: 'Recovery 70-85%. Feed pressure 10-30 bar. Antiscalant dosing. CIP required.'
  },
  {
    id: 'uf_system', name: 'UF System (Ultrafiltration)', category: 'Tertiary', type: 'Membrane',
    purpose: 'Colloids and bacteria removal', bodReduction: 0.15, codReduction: 0.20, tssReduction: 0.99, ogReduction: 0.40,
    tnReduction: 0.20, tpReduction: 0.30, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.2, footprint: 'Small', energyUse: 'Medium', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: '0.01-0.1 micron membranes. Removes colloids, bacteria, viruses.',
    notes: 'Pressure or submerged config. Flux 40-80 LMH. CIP with NaOCl + citric acid.'
  },
  {
    id: 'mf_system', name: 'MF System (Microfiltration)', category: 'Tertiary', type: 'Membrane',
    purpose: 'Suspended solids removal', bodReduction: 0.10, codReduction: 0.10, tssReduction: 0.99, ogReduction: 0.50,
    tnReduction: 0.10, tpReduction: 0.15, tdsChange: 0, phChange: 0,
    minFlow: 100, maxFlow: 100000, typicalHRT: 0.1, footprint: 'Small', energyUse: 'Medium', capitalCost: 'High', operatingCost: 'Medium',
    salineCompatible: true,
    description: '0.1-10 micron membranes. Replaces conventional filtration.',
    notes: 'Tubular or submerged hollow fibre. Low pressure < 2 bar. Backwash with permeate.'
  },
  {
    id: 'aop_uv_h2o2', name: 'AOP (UV + H2O2)', category: 'Tertiary', type: 'Advanced Oxidation',
    purpose: 'Refractory organics destruction', bodReduction: 0.20, codReduction: 0.40, tssReduction: 0, ogReduction: 0.30,
    tnReduction: 0.10, tpReduction: 0, tdsChange: 0, phChange: -0.1,
    minFlow: 50, maxFlow: 20000, typicalHRT: 0.1, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'UV + hydrogen peroxide generates hydroxyl radicals.',
    notes: 'H2O2 dose 10-50 mg/L. UV dose 500-1000 mJ/cm2. Quench residual peroxide.'
  },
  {
    id: 'aop_ozone', name: 'AOP (Ozone)', category: 'Tertiary', type: 'Gas Oxidation',
    purpose: 'Color and organics oxidation', bodReduction: 0.15, codReduction: 0.30, tssReduction: 0, ogReduction: 0.25,
    tnReduction: 0.05, tpReduction: 0, tdsChange: 0, phChange: -0.1,
    minFlow: 50, maxFlow: 20000, typicalHRT: 0.2, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'Ozone gas contactor for color, odor, and organics.',
    notes: 'O3 dose 5-15 mg/L. Contact time 10-20 min. Off-gas destruction. Residual O3 quench.'
  },
  {
    id: 'evap_mvc', name: 'Evaporator (MVC)', category: 'Tertiary', type: 'Thermal',
    purpose: 'Zero liquid discharge', bodReduction: 0.05, codReduction: 0.20, tssReduction: 0.99, ogReduction: 0.50,
    tnReduction: 0.20, tpReduction: 0.30, tdsChange: -0.95, phChange: -0.2,
    minFlow: 10, maxFlow: 5000, typicalHRT: 2, footprint: 'Medium', energyUse: 'Very High', capitalCost: 'Very High', operatingCost: 'Very High',
    salineCompatible: true,
    description: 'Mechanical vapor compression for brine concentration.',
    notes: 'Steam production from compressor. Crystallizer for solids. For high TDS > 50000 mg/L.'
  },

  // POLISHING
  {
    id: 'cartridge_filter', name: 'Cartridge Filter', category: 'Polishing', type: 'Disposable Media',
    purpose: 'Final solids capture', bodReduction: 0.02, codReduction: 0.02, tssReduction: 0.90, ogReduction: 0.05,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 5000, typicalHRT: 0.05, footprint: 'Very Small', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Medium',
    salineCompatible: true,
    description: '5-20 micron disposable cartridges. Final protection.',
    notes: 'Multiple housings in parallel. Pressure differential alarm. Changes every 1-3 months.'
  },
  {
    id: 'bag_filter', name: 'Bag Filter', category: 'Polishing', type: 'Woven Bag',
    purpose: 'Batch solids capture', bodReduction: 0.02, codReduction: 0.02, tssReduction: 0.85, ogReduction: 0.05,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 5000, typicalHRT: 0.05, footprint: 'Very Small', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: '1-200 micron woven bags in stainless housing.',
    notes: 'Single or multi-bag housings. Quick-change mechanism. Higher capacity than cartridges.'
  },
  {
    id: 'uv_disinfection', name: 'UV Disinfection', category: 'Polishing', type: 'Channel',
    purpose: 'Pathogen inactivation', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.01, footprint: 'Very Small', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'UV-C at 254nm for bacteria, virus, and protozoa inactivation.',
    notes: 'Dose > 40 mJ/cm2 for 4-log. Automatic quartz sleeve wiping. UV intensity monitoring. Transmittance > 65%.'
  },
  {
    id: 'chlorine_contact', name: 'Chlorine Contact Tank', category: 'Polishing', type: 'Channel / Tank',
    purpose: 'Chemical disinfection', bodReduction: 0.03, codReduction: 0.05, tssReduction: 0, ogReduction: 0,
    tnReduction: 0.05, tpReduction: 0, tdsChange: 0, phChange: -0.1,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.5, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Chlorine or sodium hypochlorite for residual disinfection.',
    notes: 'Contact time 30 min at peak flow. CT value for target log reduction. Dechlorination if needed.'
  },
  {
    id: 'ozone_polish', name: 'Ozone Polishing', category: 'Polishing', type: 'Gas Contact',
    purpose: 'Final oxidation and disinfection', bodReduction: 0.05, codReduction: 0.10, tssReduction: 0, ogReduction: 0.10,
    tnReduction: 0.05, tpReduction: 0, tdsChange: 0, phChange: -0.05,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.2, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'Ozone for final color, odor, micro-pollutant removal.',
    notes: '5-10 mg/L dose. 10-15 min contact. Residual quench with bisulfite.'
  },
  {
    id: 'peracetic', name: 'Peracetic Acid Disinfection', category: 'Polishing', type: 'Chemical',
    purpose: 'Chemical disinfection', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: -0.05,
    minFlow: 50, maxFlow: 50000, typicalHRT: 0.3, footprint: 'Very Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Peracetic acid for disinfection without chlorination byproducts.',
    notes: 'Dose 2-5 mg/L. Contact 15-30 min. Degrades to vinegar and water.'
  },

  // SLUDGE
  {
    id: 'gravity_thickener', name: 'Gravity Thickener', category: 'Sludge', type: 'Circular',
    purpose: 'Volume reduction by gravity', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 50000, typicalHRT: 24, footprint: 'Medium', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Circular tank with slow stirring. Thicken to 4-6% solids.',
    notes: 'Overflow rate 0.5-1.0 m³/m².day. Pickets for sludge raking. Supernatant to headworks.'
  },
  {
    id: 'daf_thickener', name: 'DAF Thickener', category: 'Sludge', type: 'Flotation',
    purpose: 'Rapid sludge thickening', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 20000, typicalHRT: 1, footprint: 'Small', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Dissolved air flotation for WAS thickening. 5-7% solids.',
    notes: 'Thicker than gravity. Higher polymer demand. Small footprint. Good for O&G sludge.'
  },
  {
    id: 'drum_thickener', name: 'Rotary Drum Thickener', category: 'Sludge', type: 'Rotary Screen',
    purpose: 'Mechanical thickening', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 5, maxFlow: 5000, typicalHRT: 0.1, footprint: 'Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Rotating drum with internal screw. 4-8% solids.',
    notes: 'Polymer conditioning required. Continuous discharge. Filtrate to headworks.'
  },
  {
    id: 'centrifuge', name: 'Centrifuge (Decanter)', category: 'Sludge', type: 'High-Speed Spin',
    purpose: 'High-solids dewatering', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 5, maxFlow: 10000, typicalHRT: 0.05, footprint: 'Small', energyUse: 'High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'Decanter centrifuge at 2000-4000 G. 18-25% cake solids.',
    notes: 'High polymer dose. Continuous operation. Noise and vibration. Centrate to headworks.'
  },
  {
    id: 'belt_press', name: 'Belt Filter Press', category: 'Sludge', type: 'Gravity + Pressure',
    purpose: 'Continuous dewatering', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 5, maxFlow: 10000, typicalHRT: 0.1, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Gravity zone + pressure rollers. 18-22% cake solids.',
    notes: 'Polymer conditioning. Belt washing. Wash water to headworks. Open or enclosed.'
  },
  {
    id: 'screw_press', name: 'Screw Press', category: 'Sludge', type: 'Screw Conveyor',
    purpose: 'Low-energy dewatering', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 1, maxFlow: 2000, typicalHRT: 0.05, footprint: 'Very Small', energyUse: 'Very Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Slow-speed screw in perforated cylinder. 20-25% cake solids.',
    notes: 'Low or no polymer. Minimal noise. Continuous discharge. Filtrate to headworks.'
  },
  {
    id: 'plate_press', name: 'Filter Press (Plate & Frame)', category: 'Sludge', type: 'Batch Pressure',
    purpose: 'High-solids batch dewatering', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 1, maxFlow: 5000, typicalHRT: 4, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Recessed plate press at 8-16 bar. 20-30% cake solids.',
    notes: 'Batch operation. Chemical conditioning (lime + ferric). Cake drops automatically. High labor.'
  },
  {
    id: 'drying_bed', name: 'Sludge Drying Bed', category: 'Sludge', type: 'Sand Bed',
    purpose: 'Natural solar drying', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 1, maxFlow: 2000, typicalHRT: 720, footprint: 'Very Large', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Paved or sand beds for solar evaporation. 35-45% solids.',
    notes: '30-day drying cycle. Greenhouse option. Minimal energy. Large land requirement.'
  },
  {
    id: 'thermal_dryer', name: 'Thermal Dryer', category: 'Sludge', type: 'Rotary / Belt',
    purpose: 'Reduce sludge to pellets', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 1, maxFlow: 5000, typicalHRT: 1, footprint: 'Medium', energyUse: 'Very High', capitalCost: 'High', operatingCost: 'High',
    salineCompatible: true,
    description: 'Direct or indirect thermal drying. 90%+ solids (pellets).',
    notes: 'Biosolids class A potential. Energy intensive. Dust control. Product for fertilizer.'
  },
  {
    id: 'digester_anaerobic', name: 'Anaerobic Digester', category: 'Sludge', type: 'Heated Tank',
    purpose: 'Stabilization + biogas', bodReduction: 0.40, codReduction: 0.35, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0.2,
    minFlow: 10, maxFlow: 50000, typicalHRT: 480, footprint: 'Large', energyUse: 'Low', capitalCost: 'High', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Mesophilic (35°C) or thermophilic (55°C) digestion.',
    notes: '20-day HRT. Biogas 60% CH4. CHP for energy recovery. Digested sludge to dewatering.'
  },
  {
    id: 'digester_aerobic', name: 'Aerobic Digester', category: 'Sludge', type: 'Aerated Tank',
    purpose: 'Stabilization', bodReduction: 0.30, codReduction: 0.25, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: -0.1,
    minFlow: 10, maxFlow: 50000, typicalHRT: 360, footprint: 'Large', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Extended aeration of sludge for stabilization.',
    notes: '15-20 day HRT. Lower VS reduction than anaerobic. No biogas. Simpler operation.'
  },

  // ADDITIONAL PRE-TREATMENT & MID-STAGE
  {
    id: 'cpi_separator', name: 'CPI Oil-Water Separator', category: 'Primary', type: 'Corrugated Plate Interceptor',
    purpose: 'Free oil and light solids separation via parallel plates', bodReduction: 0.10, codReduction: 0.08, tssReduction: 0.25, ogReduction: 0.85,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 50000, typicalHRT: 1.5, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Corrugated plate packs (45° angle) increase oil separation surface area 5-10x. Free oil droplets (>150 micron) rise and coalesce on plates, then drain to an oil collection trough. Handles free oil, light floating solids, and settleable grit in one unit.',
    notes: 'Plate spacing 20-50mm. Oil removal efficiency 85-95% for free oil. Does NOT remove emulsified oil. Best for API separator upgrade or as pre-treatment before DAF. Oil layer < 25mm triggers skimmer. Can include steam heating for high-viscosity oil (food processing).'
  },
  {
    id: 'ph_adjust_tank', name: 'pH Adjustment Tank', category: 'Primary', type: 'Mixing / Reaction',
    purpose: 'Mid-stage pH neutralization for biological protection', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0.05, tdsChange: 0.01, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.5, footprint: 'Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Rapid mix tank with online pH probe and automated chemical dosing. Used mid-process when primary treatment changes pH (e.g. acid wastewater after coagulation, or alkaline effluent before biology). Protects downstream biological processes from pH shock.',
    notes: 'HRT 15-30 minutes. Mixer 150-300 rpm. pH probe with 4-20mA output. Dosing pumps with variable speed. Buffer tank recommended after for mixing completeness. Dose: lime 50-200 mg/L, caustic 20-100 mg/L, or sulfuric acid 10-50 mg/L depending on direction.'
  },
  {
    id: 'aeration_basin', name: 'Aeration Basin / Blower System', category: 'Secondary', type: 'Diffused Air',
    purpose: 'Oxygen transfer for biological oxidation and odor control', bodReduction: 0.15, codReduction: 0.12, tssReduction: 0, ogReduction: 0.10,
    tnReduction: 0.05, tpReduction: 0, tdsChange: -0.01, phChange: -0.1,
    minFlow: 100, maxFlow: 100000, typicalHRT: 4, footprint: 'Medium', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'Fine or coarse bubble diffusers with rotary lobe blowers. Provides oxygen for aerobic biological processes AND can be used for pre-aeration (odor stripping, Fe²⁺ oxidation, CO₂ stripping). Essential when BOD > 500 mg/L or when nitrification is required.',
    notes: 'SOTE 4-8% for coarse bubble, 25-35% for fine bubble. Air flow 8-12 Nm3/kg BOD removed. Blower pressure 0.4-0.8 bar. Pre-aeration: 15-30 min at 5-10 mg/L DO. Can include anoxic zone with mixers for denitrification. Consider heat recovery from blower exhaust.'
  },
  {
    id: 'aerated_equalization', name: 'Aerated Equalization Tank', category: 'Pre-Treatment', type: 'Mixed / Aerated',
    purpose: 'Flow balancing with pre-aeration and odor control', bodReduction: 0.05, codReduction: 0.03, tssReduction: 0, ogReduction: 0.05,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: -0.05,
    minFlow: 50, maxFlow: 50000, typicalHRT: 8, footprint: 'Large', energyUse: 'Medium', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Equalization tank with continuous aeration. Prevents septicity, strips volatile odors (H₂S, mercaptans), oxidizes ferrous iron, and provides partial BOD removal. Critical for food processing, dairy, and brewery wastewater where organic acids cause low pH and odor.',
    notes: '8-16 hr HRT. DO 0.5-1.5 mg/L (not full aeration — just mixing + odor control). Fine bubble diffusers at 0.3m spacing. pH probe in tank. Overflow to downstream treatment. Can add pH adjustment chemical dosing inline. Emergency bypass required.'
  },

  // BUFFER & STORAGE TANKS
  {
    id: 'buffer_tank', name: 'Buffer / Holding Tank', category: 'Pre-Treatment', type: 'Storage',
    purpose: 'Flow balancing and peak flow attenuation', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 100000, typicalHRT: 4, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Steel or concrete tank for flow balancing. Dampens diurnal flow peaks from 1:3 to 1:1.5 ratio. Simple mixer at 1-2 rpm to prevent settling. Level sensors control inlet pump and outlet flow. Volume sized at 2-6 hours HRT based on peak flow coefficient.',
    notes: 'HRT = 0.25 × (Qpeak / Qavg - 1) × 24 hr. Min 2 hr for steady industrial, max 6 hr for highly variable (food processing). Depth 4-6 m. Mixer: 1-2 rpm side-entry or jet. Level: ultrasonic or pressure transmitter. Overflow to emergency storage.'
  },
  {
    id: 'chemical_storage_tank', name: 'Chemical Storage / Dosing Station', category: 'Chemical', type: 'Bunded Tank',
    purpose: 'Bulk chemical storage with automated dosing', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 100000, typicalHRT: 0, footprint: 'Small', energyUse: 'Low', capitalCost: 'Low', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Bunded polyethylene or stainless steel tanks (110% containment) for bulk chemical storage. Typically 7-30 days supply. Dosing pumps (diaphragm or peristaltic) with variable speed. Calibration cylinders for dose verification. pH-resistant materials for acid/alkali.',
    notes: 'Ferric chloride: PE tank, FRP bund. Caustic soda: PE or stainless (316). Polymer: stainless or PE with low-shear mixer. Lime: steel silo with dust collector. Chlorine: dedicated ventilated room with safety shower. Spill containment: 110% of largest tank.'
  },
  {
    id: 'sludge_holding_tank', name: 'Sludge Holding / Storage Tank', category: 'Sludge', type: 'Concrete / Steel',
    purpose: 'Temporary sludge storage before dewatering or disposal', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 5, maxFlow: 50000, typicalHRT: 48, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Tank for temporary sludge storage between process and dewatering. Volume sized for 1-3 days sludge production at design solids loading. Allows batch dewatering operations. Mixing at 0.5-1.0 rpm prevents septicity and maintains homogeneity.',
    notes: 'Volume = daily sludge volume × 1-3 days. Depth 3-5 m. Mixing: slow-speed impeller or jet. Cover recommended for odor control (fixed or floating). Supernatant overflow to headworks. Grit sump at bottom. Access manway for cleaning.'
  },
  {
    id: 'backwash_tank', name: 'Backwash Water Tank', category: 'Tertiary', type: 'Storage',
    purpose: 'Store filtered water for filter backwashing', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.5, footprint: 'Small', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Tank storing filtered effluent for backwashing of MMF, cloth filters, or sand filters. Volume = 2-3 backwash cycles. Backwash flow is typically 10-15 m/hr for 10-15 minutes per filter. Clean water prevents recontamination during backwash.',
    notes: 'Volume = Afilter × vbackwash × tbackwash × n. Typical: 10-15 m/hr × 10-15 min per filter. 2-3 backwashes stored. Elevated tank or pump to provide 5-8 m head for air scour + water wash. Level control with auto-refill from downstream.'
  },
  {
    id: 'emergency_tank', name: 'Emergency / Storm Overflow Tank', category: 'Pre-Treatment', type: 'Concrete',
    purpose: 'Contain flows exceeding design capacity or process upsets', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 2, footprint: 'Large', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Tank for containing process upsets, power failures, or peak storm flows exceeding treatment capacity. Sized for 1.5-3× design flow for 2-4 hours. Concrete construction with emergency discharge to controlled outfall. Prevents uncontrolled bypass to environment.',
    notes: 'Volume = Qdesign × 2-4 hr × safety factor 1.5. Overflow weir level control. Alarm at 70% level. Emergency pump returns to headworks when capacity available. Storm tanks may be 10-20 hr HRT. Aeration optional to prevent odor. Disinfection before emergency discharge.'
  },
  {
    id: 'effluent_polishing_tank', name: 'Effluent Polishing / Contact Tank', category: 'Polishing', type: 'Concrete / Steel',
    purpose: 'Extended contact time for disinfection and final settling', bodReduction: 0.02, codReduction: 0.03, tssReduction: 0.05, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.5, footprint: 'Medium', energyUse: 'Very Low', capitalCost: 'Low', operatingCost: 'Very Low',
    salineCompatible: true,
    description: 'Final tank providing 20-60 minutes contact time after disinfection (chlorine/UV). Also provides gravity settling of any residual floc before discharge. Serpentine baffles prevent short-circuiting. Sampling points at inlet, midpoint, and outlet for compliance monitoring.',
    notes: 'HRT 20-60 min at peak flow. Baffled channels prevent short-circuiting (t10/t = 0.5-0.7). Depth 3-4 m. Sampling: auto-composite or grab. pH probe at outlet. Flow measurement (weir, flume, or magmeter). Discharge compliance monitoring: continuous or spot sampling.'
  },

  // DISINFECTION
  {
    id: 'uv_led', name: 'UV-LED System', category: 'Disinfection', type: 'Channel',
    purpose: 'Mercury-free UV disinfection', bodReduction: 0, codReduction: 0, tssReduction: 0, ogReduction: 0,
    tnReduction: 0, tpReduction: 0, tdsChange: 0, phChange: 0,
    minFlow: 10, maxFlow: 5000, typicalHRT: 0.01, footprint: 'Very Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Low',
    salineCompatible: true,
    description: 'Solid-state UV-LED at 265-280nm. Instant on/off.',
    notes: 'No mercury. Instant start. Lower energy than lamps. Longer life 10,000+ hours.'
  },
  {
    id: 'chlorine_dioxide', name: 'Chlorine Dioxide', category: 'Disinfection', type: 'Chemical',
    purpose: 'Broad-spectrum disinfection', bodReduction: 0.03, codReduction: 0.05, tssReduction: 0, ogReduction: 0,
    tnReduction: 0.05, tpReduction: 0, tdsChange: 0, phChange: -0.05,
    minFlow: 50, maxFlow: 100000, typicalHRT: 0.5, footprint: 'Small', energyUse: 'Low', capitalCost: 'Medium', operatingCost: 'Medium',
    salineCompatible: true,
    description: 'ClO2 generated on-site. Less THM formation than chlorine.',
    notes: 'Dose 1-2 mg/L. 30 min contact. Generated from sodium chlorite + acid. Explosive gas precautions.'
  },
];
